import { Building2, ChevronRight } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, PageHeader, SectionLabel } from '../components/ui';
import { legislatorsAPI } from '../lib/api';
import { initialsOf, partyColor } from '../lib/utils';
import { formatPartyDisplay } from '../../utils/partyUtils';

interface Legislator {
  id: string;
  full_name: string;
  party_affiliation?: string | null;
  chamber?: string | null;
  district?: string | null;
  district_display?: string | null;
  photo_url?: string | null;
  committees?: Array<{ name: string; chamber?: string | null; role?: string | null }>;
  /*
    GET /legislators/public/:id now returns both periods under `voting`, each
    with the shared category breakdown, replacing the flat `votingRecord`. No
    back-compat alias was kept: Citizen has no users yet, so a shim would have
    been dead code from the day it shipped.
  */
  voting?: {
    session: { totalVotes: number; label: string | null; categories: VoteCategoryLite[] };
    career: { totalVotes: number; categories: VoteCategoryLite[] };
  };
  sponsoredBills?: { total: number; passed: number; failed: number };
  recentBills?: Array<{ number: string; title: string }>;
}

/*
  Category colours, keeping this screen's existing palette exactly. Kept as local
  hexes rather than the desktop's --vote-* tokens, which are scoped to
  .citizen-root.citizen-desktop and would resolve to nothing in the native shell.
*/
interface VoteCategoryLite {
  key: string;
  label: string;
  percent: number;
}
const VOTE_COLORS: Record<string, string> = {
  yes: '#5A8F5A',
  no: '#C97B5A',
  excused: '#D4D0C8',
  absent: '#D4D0C8',
  other: '#D4D0C8',
};
const voteColor = (key: string) => VOTE_COLORS[key] || '#D4D0C8';

export default function LegislatorScreen() {
  const navigate = useNavigate();
  const { id = '' } = useParams();

  const { data: leg, isLoading } = useQuery<Legislator>(['citizen-legislator', id], async () => {
    const { data } = await legislatorsAPI.getPublicProfile(id);
    return data.data;
  });

  if (isLoading || !leg) {
    return (
      <div className="flex flex-col screen-enter">
        <PageHeader title="Legislator" onBack={() => navigate(-1)} />
        <p className="text-sm text-[color:var(--muted-foreground)] px-6 py-10">Loading…</p>
      </div>
    );
  }

  // This session by default — a citizen asking about their representative means
  // now. The career figure is on the desktop profile behind an explicit switch.
  const vr = leg.voting?.session;
  const committees = leg.committees || [];
  const recent = leg.recentBills || [];

  return (
    <div className="flex flex-col screen-enter">
      <PageHeader title="Legislator" onBack={() => navigate(-1)} />

      <div className="flex-1 pb-24">
        {/* Hero */}
        <div
          className="relative text-white pt-4 pb-7 px-6 rounded-b-[32px] overflow-hidden"
          style={{ background: 'linear-gradient(135deg, var(--hero-deep-3), var(--hero-deep) 55%, var(--hero-deep-2))' }}
        >
          <div className="absolute inset-0 topo-overlay pointer-events-none" />
          <div className="relative flex items-start gap-4">
            <div
              className="h-20 w-20 shrink-0 rounded-full overflow-hidden flex items-center justify-center font-bold text-2xl"
              style={{
                background: 'rgba(255,255,255,0.12)',
                color: 'var(--hero-light-accent)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              {leg.photo_url ? (
                <img src={leg.photo_url} alt={leg.full_name} className="w-full h-full object-cover" />
              ) : (
                initialsOf(leg.full_name)
              )}
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h1 className="text-[26px] font-extrabold leading-tight tracking-tight">{leg.full_name}</h1>
              <p className="text-sm text-white/75 mt-1">
                {leg.chamber}
                {leg.district_display ? ` · ${leg.district_display}` : leg.district ? ` · District ${leg.district}` : ''} ·
                Colorado
              </p>
              {leg.party_affiliation && (
                <div className="mt-3 inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs font-medium">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: partyColor(leg.party_affiliation) }} />
                  {formatPartyDisplay(leg.party_affiliation)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Voting record */}
        {vr && vr.totalVotes > 0 && (
          <div className="px-6 mt-5">
            <SectionLabel right={<span className="text-xs text-[color:var(--muted-foreground)]">{vr.totalVotes} votes this session</span>}>
              Voting record
            </SectionLabel>
            <Card className="p-4 border-0">
              <div className="flex h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
                {vr.categories.map((cat) => (
                  <div key={cat.key} style={{ width: `${cat.percent}%`, background: voteColor(cat.key) }} />
                ))}
              </div>
              <div className="flex items-center justify-between mt-3 text-xs">
                {vr.categories.map((cat) => (
                  <span key={cat.key} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: voteColor(cat.key) }} />
                    {cat.label} <b>{cat.percent}%</b>
                  </span>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Sponsored bills stat */}
        {leg.sponsoredBills && leg.sponsoredBills.total > 0 && (
          <div className="px-6 mt-5">
            <div className="grid grid-cols-3 gap-3">
              <StatCard label="Sponsored" value={leg.sponsoredBills.total} />
              <StatCard label="Passed" value={leg.sponsoredBills.passed} />
              <StatCard label="Failed" value={leg.sponsoredBills.failed} />
            </div>
          </div>
        )}

        {/* Committees */}
        {committees.length > 0 && (
          <div className="px-6 mt-5">
            <SectionLabel>Committees</SectionLabel>
            <Card className="divide-y border-0 overflow-hidden">
              {committees.map((co, i) => {
                const lead = /chair/i.test(co.role || '');
                return (
                  <div key={i} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center"
                        style={{
                          background: lead ? 'var(--accent)' : 'var(--secondary)',
                          color: lead ? 'var(--primary)' : 'var(--muted-foreground)',
                        }}
                      >
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{co.name}</p>
                        {co.role && <p className="text-xs text-[color:var(--muted-foreground)] mt-0.5">{co.role}</p>}
                      </div>
                    </div>
                    {lead && <Badge variant="accent">Leadership</Badge>}
                  </div>
                );
              })}
            </Card>
          </div>
        )}

        {/* Recent bills */}
        {recent.length > 0 && (
          <div className="px-6 mt-5">
            <SectionLabel>Recent bills</SectionLabel>
            <Card className="divide-y border-0 overflow-hidden">
              {recent.slice(0, 8).map((b) => (
                <button
                  key={b.number}
                  onClick={() => navigate(`/citizen/bill/${b.number}`)}
                  className="w-full p-4 flex items-center gap-3 hover-elevate text-left"
                >
                  <span
                    className="text-[11px] font-bold px-2 py-0.5 rounded-md shrink-0"
                    style={{ background: 'var(--primary)', color: 'white' }}
                  >
                    {b.number}
                  </span>
                  <span className="text-sm font-medium flex-1 truncate">{b.title}</span>
                  <ChevronRight className="w-4 h-4 text-[color:var(--muted-foreground)] shrink-0" />
                </button>
              ))}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card className="p-4 border-0 text-center">
      <div className="text-2xl font-extrabold" style={{ color: 'var(--primary)' }}>
        {value}
      </div>
      <div className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--muted-foreground)] mt-1">
        {label}
      </div>
    </Card>
  );
}
